import { vacancies as staticVacancies } from "./vacancies";

export const CV_ACCEPT = ".pdf,.doc,.docx,.jpg,.jpeg,.png";
export const CV_MAX_BYTES = 8 * 1024 * 1024;

const CV_EXTENSIONS = CV_ACCEPT.split(",").map((ext) => ext.trim().toLowerCase());

/**
 * The native file input's `accept` attribute only filters the OS picker —
 * drag-and-drop bypasses it entirely, so this needs to be checked explicitly
 * wherever a file is accepted (picker or drop).
 */
export function isAcceptedCvFile(file) {
  if (!file) return false;
  const name = String(file.name || "").toLowerCase();
  return CV_EXTENSIONS.some((ext) => name.endsWith(ext));
}

export function jobOpeningPath(job) {
  const key = job?.slug || job?.id;
  return key ? `/stillinger/${key}` : "/stillinger";
}

/** Homepage notice: nothing while loading; hide when the list is empty. */
export function shouldShowJobsNotice({ loading, items }) {
  if (loading) return false;
  return Array.isArray(items) && items.length > 0;
}

export function jobsNoticeTitle(count) {
  const n = Number(count) || 0;
  if (n === 1) return "Vi har 1 ledig stilling";
  return `Vi har ${n} ledige stillinger`;
}

export function jobApplyPath(job) {
  const path = jobOpeningPath(job);
  return path === "/stillinger" ? path : `${path}#soknad`;
}

export function splitFullName(fullName) {
  const parts = String(fullName || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { first_name: "", last_name: "" };
  if (parts.length === 1) return { first_name: parts[0], last_name: "" };
  return { first_name: parts[0], last_name: parts.slice(1).join(" ") };
}

export function formatCvSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function mapStaticJobOpenings() {
  return staticVacancies.map((vacancy) => ({
    id: vacancy.id,
    slug: vacancy.id,
    title: vacancy.title,
    short_description: vacancy.description,
    employment_type_label: "Heltid",
    location: "Norge",
    path: vacancy.path,
    _static: true,
    _staticContent: vacancy.content,
  }));
}

export function staticJobOpeningById(id) {
  if (!id) return null;
  const key = String(id);
  return (
    mapStaticJobOpenings().find(
      (job) => String(job.id) === key || String(job.slug) === key
    ) || null
  );
}

export function isPublicJobOpening(payload) {
  return Boolean(payload && !payload.error && (payload.id || payload.title));
}

export function formatJobDate(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("nb-NO", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function requirementItemsFromJob(job) {
  const raw = Array.isArray(job?.requirement_items) ? job.requirement_items : [];
  const items = raw
    .map((item) => ({
      id: item?.id || item?.text,
      text: String(item?.text || "").trim(),
    }))
    .filter((item) => item.text);
  if (items.length) return items;
  return String(job?.requirements || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^[-•*]\s*/, "").trim())
    .filter(Boolean)
    .map((text, index) => ({ id: `static-${index}`, text }));
}

function normalizePublicContact(item) {
  return {
    name: String(item?.name || "").trim(),
    email: String(item?.email || "").trim(),
    phone: String(item?.phone || "").trim(),
  };
}

function contactKey(contact) {
  const email = String(contact?.email || "").trim().toLowerCase();
  if (email) return `email:${email}`;
  const name = String(contact?.name || "").trim().toLowerCase();
  const phone = String(contact?.phone || "").trim();
  return `name:${name}|phone:${phone}`;
}

function hasContactData(contact) {
  return Boolean(contact?.name || contact?.email || contact?.phone);
}

export function publicJobOpeningContact(job) {
  const fromObject = normalizePublicContact(job?.contact);
  return hasContactData(fromObject) ? fromObject : null;
}

export function publicJobOtherContacts(job) {
  const raw = Array.isArray(job?.other_contacts)
    ? job.other_contacts
    : Array.isArray(job?.responsibles)
      ? job.responsibles
      : [];
  const opening = publicJobOpeningContact(job);
  const openingKey = opening ? contactKey(opening) : null;
  const seen = new Set(openingKey ? [openingKey] : []);

  return raw.map(normalizePublicContact).filter((contact) => {
    if (!hasContactData(contact)) return false;
    const key = contactKey(contact);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function publicJobContacts(job) {
  const opening = publicJobOpeningContact(job);
  const others = publicJobOtherContacts(job);
  return opening ? [opening, ...others] : others;
}

export function firstApiError(payload) {
  if (!payload) return "Noe gikk galt.";
  if (typeof payload.error === "string" && payload.error) return payload.error;
  const errors = payload.errors;
  if (Array.isArray(errors) && errors[0]) {
    return typeof errors[0] === "string" ? errors[0] : String(errors[0]);
  }
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).flat()[0];
    if (first) return String(first);
  }
  return null;
}
